import { useMutation } from '@tanstack/react-query';
import z from 'zod';
import { logUnknownErrorResponse } from '@hungryroot/o11y';
import {
  type CustomerPaymentResponse,
  type CustomerPaymentPayload,
} from '@hungryroot/rest';
import {
  postApiV2CustomersCustomerIdPayment,
  type SavePayment400Problem,
  type SavePayment403Problem,
} from '@hungryroot/rest/api/v2/customers/[id]/payment/post/post-api-v2-customers-customer_id-payment';

export const CustomerPaymentSchema = z.object({
  stripe_token: z.string({
    required_error: 'Please enter a valid card number.',
  }),
});

type SavePaymentProblem = SavePayment400Problem | SavePayment403Problem;

// typed Error you will throw
export class SavePaymentError extends Error {
  status: 400 | 403;
  data: SavePaymentProblem;
  messages: string[];
  constructor(status: 400 | 403, data: SavePaymentProblem) {
    const msgs = Array.isArray(data.detail) ? data.detail : [data.detail];
    super(msgs.join('; '));
    this.name = 'SavePaymentError';
    this.status = status;
    this.data = data;
    this.messages = msgs;
  }
}

export function useSavePaymentMethod(options?: {
  onSuccess?: () => void;
  onError?: (error: SavePaymentError) => void;
}) {
  return useMutation<
    CustomerPaymentResponse,
    SavePaymentError,
    CustomerPaymentPayload
  >({
    mutationFn: async (payload) => {
      const resp = await postApiV2CustomersCustomerIdPayment(payload);
      if (resp.status === 200) return resp.data;

      throw new SavePaymentError(resp.status, resp.data);
    },
    onSuccess: options?.onSuccess,
    onError: (error, variables) => {
      const stringifiedErrorResponse = JSON.stringify(error.data, null, 2);

      logUnknownErrorResponse('Failed to save payment method', {
        key: 'useSavePaymentMethod',
        error: stringifiedErrorResponse,
        payload: JSON.stringify(variables),
      });

      options?.onError?.(error);
    },
  });
}
